import React, { useState } from 'react';
import { Box, Grid, Card, CardContent, Typography, Avatar, List, ListItemText, ListItemIcon, ListItemButton, Chip } from '@mui/material';
import { Contacts as ContactsIcon, Phone as PhoneIcon, Email as EmailIcon, Add as AddIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { PageHeader, EmptyState, LoadingSpinner, DataCard, SearchInput } from '../../components/common';
import { ContactFormDialog } from '../../components/dialogs';
import { useFamilyData } from '../../hooks';
import { colors } from '../../theme';

// Map role keys to display labels
const ROLE_LABELS: Record<string, string> = {
  family: 'Family',
  friend: 'Friend',
  teacher: 'Teacher',
  coach: 'Coach',
  doctor: 'Doctor',
  dentist: 'Dentist',
  babysitter: 'Babysitter',
  pharmacy: 'Pharmacy',
  school: 'School',
  neighbor: 'Neighbor',
  emergency: 'Emergency',
  other: 'Other',
};

const ContactsPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { items: contacts, loading, insert, update, remove } = useFamilyData('contact');
  const [search, setSearch] = useState('');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<any>(null);

  const handleOpenNew = () => {
    setSelectedContact(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (contact: any) => {
    setSelectedContact(contact);
    setDialogOpen(true);
  };

  const handleSave = async (data: any, familyIds: string[]) => {
    try {
      if (selectedContact?.id) {
        // When editing, just update the existing contact
        await update(selectedContact.id, data);
        enqueueSnackbar('Contact updated', { variant: 'success' });
      } else {
        // Insert into each selected family
        for (const familyId of familyIds) {
          await insert(data, familyId);
        }
        enqueueSnackbar('Contact added', { variant: 'success' });
      }
    } catch (err) {
      enqueueSnackbar('Failed to save contact', { variant: 'error' });
      throw err;
    }
  };

  const handleDelete = async () => {
    if (!selectedContact?.id) return;
    try {
      await remove(selectedContact.id);
      enqueueSnackbar('Contact deleted', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Failed to delete contact', { variant: 'error' });
      throw err;
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading contacts..." />;
  }

  // Filter out schools (they're shown in SchoolPage) and apply search
  const filteredContacts = contacts.filter((contact) => {
    const data = (contact.data?.data || {}) as any;
    // Exclude school contacts
    if (data.category?.toLowerCase() === 'school') return false;
    const name = `${data.first || ''} ${data.last || ''}`.toLowerCase();
    return name.includes(search.toLowerCase());
  });

  return (
    <Box>
      <PageHeader
        title="Contacts"
        subtitle="Family contacts directory"
        actionLabel="Add Contact"
        actionIcon={AddIcon}
        onAction={handleOpenNew}
      />

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DataCard
            title="Contacts"
            icon={ContactsIcon}
            iconColor={colors.contacts}
            value={contacts.length}
            onClick={handleOpenNew}
          />
        </Grid>

        <Grid size={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">All Contacts</Typography>
                <SearchInput
                  value={search}
                  onChange={setSearch}
                  placeholder="Search contacts..."
                />
              </Box>

              {filteredContacts.length === 0 ? (
                <EmptyState
                  icon={ContactsIcon}
                  title={search ? 'No contacts found' : 'No contacts yet'}
                  description={search ? 'Try a different search term' : 'Add contacts like teachers, doctors, and coaches'}
                  actionLabel={search ? undefined : 'Add Contact'}
                  onAction={search ? undefined : handleOpenNew}
                />
              ) : (
                <List disablePadding>
                  {filteredContacts.map((contact) => {
                    const data = (contact.data?.data || {}) as any;
                    const name = `${data.first || ''} ${data.last || ''}`.trim() || 'Unknown';
                    const initials = `${data.first?.[0] || ''}${data.last?.[0] || ''}`.toUpperCase() || '?';

                    return (
                      <ListItemButton
                        key={contact.id}
                        sx={{ borderBottom: 1, borderColor: 'divider', borderRadius: 1 }}
                        onClick={() => handleOpenEdit(contact)}
                      >
                        <ListItemIcon>
                          <Avatar sx={{ bgcolor: colors.contacts }}>{initials}</Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {name}
                              {data.organization && <Typography variant="caption" color="text.secondary">({data.organization})</Typography>}
                            </Box>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                              {data.phone && (
                                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <PhoneIcon fontSize="inherit" /> {data.phone}
                                </Typography>
                              )}
                              {data.email && (
                                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <EmailIcon fontSize="inherit" /> {data.email}
                                </Typography>
                              )}
                              {(data.city || data.state) && (
                                <Typography variant="body2" color="text.secondary">
                                  {[data.city, data.state].filter(Boolean).join(', ')}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                        {data.role && <Chip label={ROLE_LABELS[data.role] || data.role} size="small" />}
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <ContactFormDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setSelectedContact(null);
        }}
        onSave={handleSave}
        onDelete={selectedContact ? handleDelete : undefined}
        contact={selectedContact}
      />
    </Box>
  );
};

export default ContactsPage;
